/**
 * NIP-48 `proxy` — this event came from somewhere else.
 *
 * A bridge republishes a post from another network onto Nostr under a key the
 * bridge controls, and tags it with where it came from. Surfacing that is a
 * correctness issue rather than a nicety: without it a reader has no way to tell an
 * account that publishes to Nostr from a mirror of an account that does not, and
 * the difference decides whether replying to it will ever be seen. A reply to a
 * one-way bridge goes nowhere, and the reader has no way to know.
 *
 * The rendering rule that follows: a proxy badge is about *provenance*, so it must
 * never be styled like a verification. It says where the content is from, not that
 * anyone checked it — the bridge's signature proves the bridge published it and
 * nothing at all about the person being mirrored.
 */

import { getTagged, type HasTags } from "./tags";

/** Where a proxied event originally lived. */
export interface ProxyRef {
  /** The source identifier, as published: a URL, a URI, or an opaque id. */
  readonly id: string;
  /**
   * The source protocol, lowercased. `activitypub`, `atproto`, `rss` and `web` are
   * the named ones; anything else is passed through rather than rejected, because a
   * protocol this build has not heard of is still worth telling the reader about.
   */
  readonly protocol: string;
}

/**
 * The event's proxy reference, or undefined when it is native to Nostr.
 *
 * Both fields are required. A `proxy` tag with an id and no protocol cannot be
 * described to a reader — "from somewhere" is not information — and one with a
 * protocol and no id names a network without naming the post, which a reader can
 * neither follow nor check. Either half alone is dropped rather than half-rendered.
 *
 * First complete tag wins. An event carrying two proxy tags is malformed, and
 * picking by position keeps the badge stable across reloads instead of depending on
 * tag order.
 */
export function proxyRef(event: HasTags): ProxyRef | undefined {
  for (const tag of getTagged(event, "proxy")) {
    const id = tag[1]?.trim();
    const protocol = tag[2]?.trim().toLowerCase();
    if (!id || !protocol) continue;
    return { id, protocol };
  }
  return undefined;
}

/** How to name a source protocol in a badge. Unknown values are shown as sent. */
export function proxyProtocolLabel(protocol: string): string {
  switch (protocol) {
    case "activitypub":
      return "ActivityPub";
    case "atproto":
      return "AT Protocol";
    case "rss":
      return "RSS";
    case "web":
      return "the web";
    default:
      return protocol;
  }
}
